import React, { useEffect, useRef, useCallback, useState } from 'react';
import {
  emptyState, applyOp, denormalizePoint, normalizePoint,
  makeStroke, makeShape, makeText, makeSetBackground, makeClear, newId,
} from './whiteboardOps';
import './whiteboard.css';

// Alle Muster-Positionen sind als Bruchteile der Canvas-Größe berechnet,
// damit Mod und Schüler dasselbe proportionale Raster sehen.
function drawBackground(ctx, w, h, background, imageCache) {
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  const kind = background?.kind || 'blank';
  ctx.strokeStyle = '#c8d6e5';
  ctx.lineWidth = 1;

  if (kind === 'grid') {
    // 50 Spalten, proportionale Zeilen → gleiche normalisierte Positionen auf jedem Canvas
    const COLS = 50;
    const ROWS = Math.round(COLS * h / w);
    for (let i = 1; i < COLS; i++) {
      const x = (i / COLS) * w;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
    }
    for (let j = 1; j < ROWS; j++) {
      const y = (j / ROWS) * h;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }
  } else if (kind === 'dots') {
    const COLS = 36;
    const ROWS = Math.round(COLS * h / w);
    ctx.fillStyle = '#b0bec5';
    for (let i = 1; i < COLS; i++) {
      const x = (i / COLS) * w;
      for (let j = 1; j < ROWS; j++) {
        const y = (j / ROWS) * h;
        ctx.beginPath(); ctx.arc(x, y, 1.6, 0, Math.PI * 2); ctx.fill();
      }
    }
  } else if (kind === 'staff') {
    // Notenlinien: Abstände normalisiert auf Canvas-Höhe
    const groupGap = h / 10;   // 10 Gruppen über die Höhe
    const lineGap  = groupGap * 0.14;
    const startY   = groupGap * 0.4;
    for (let gy = startY; gy < h; gy += groupGap) {
      for (let i = 0; i < 5; i++) {
        const y = gy + i * lineGap;
        ctx.beginPath(); ctx.moveTo(0.02 * w, y); ctx.lineTo(0.98 * w, y); ctx.stroke();
      }
    }
  } else if (kind === 'image' && background.imageUrl) {
    const img = imageCache.current[background.imageUrl];
    if (img && img.complete) {
      const scale = Math.min(w / img.width, h / img.height);
      const dw = img.width * scale, dh = img.height * scale;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    }
  }
  ctx.restore();
}

function drawOp(ctx, w, h, op) {
  if (op.type === 'stroke') {
    ctx.save();
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = op.eraser ? '#ffffff' : op.color;
    ctx.lineWidth = op.width;
    ctx.beginPath();
    op.points.forEach((p, i) => {
      const { x, y } = denormalizePoint(p.x, p.y, w, h);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.restore();
  } else if (op.type === 'shape') {
    ctx.save();
    ctx.strokeStyle = op.color; ctx.lineWidth = op.width;
    const a = denormalizePoint(op.x1, op.y1, w, h);
    const b = denormalizePoint(op.x2, op.y2, w, h);
    ctx.beginPath();
    if (op.kind === 'line' || op.kind === 'arrow') {
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      if (op.kind === 'arrow') {
        const ang = Math.atan2(b.y - a.y, b.x - a.x), len = 14;
        ctx.beginPath();
        ctx.moveTo(b.x, b.y);
        ctx.lineTo(b.x - len * Math.cos(ang - Math.PI / 6), b.y - len * Math.sin(ang - Math.PI / 6));
        ctx.moveTo(b.x, b.y);
        ctx.lineTo(b.x - len * Math.cos(ang + Math.PI / 6), b.y - len * Math.sin(ang + Math.PI / 6));
        ctx.stroke();
      }
    } else if (op.kind === 'rect') {
      ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
    } else if (op.kind === 'ellipse') {
      ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2, Math.abs(b.y - a.y) / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  } else if (op.type === 'text') {
    ctx.save();
    ctx.fillStyle = op.color;
    ctx.font = `${op.size}px sans-serif`;
    ctx.textBaseline = 'top';
    const { x, y } = denormalizePoint(op.x, op.y, w, h);
    ctx.fillText(op.content, x, y);
    ctx.restore();
  }
}

export default function Whiteboard({ socketRef, roomId, readOnly = true, onUploadImage }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const stateRef = useRef(emptyState());
  const imageCache = useRef({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    drawBackground(ctx, w, h, stateRef.current.background, imageCache);
    stateRef.current.ops.forEach((op) => drawOp(ctx, w, h, op));
  }, []);

  const ensureImage = useCallback((url) => {
    if (!url || imageCache.current[url]) return;
    const img = new Image();
    img.onload = () => render();
    img.src = url;
    imageCache.current[url] = img;
  }, [render]);

  const setState = useCallback((next) => {
    stateRef.current = next;
    if (next.background?.kind === 'image') ensureImage(next.background.imageUrl);
    render();
  }, [render, ensureImage]);

  // Canvas-Größe = CSS-Größe des Canvas-Elements (nach aspect-ratio Letterboxing).
  // ResizeObserver auf Canvas selbst: feuert erst nach CSS-Layout, garantiert korrekte Werte.
  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        canvas.width = Math.floor(rect.width);
        canvas.height = Math.floor(rect.height);
        render();
      }
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    return () => ro.disconnect();
  }, [render]);

  // Pseudo-Vollbild statt nativer Fullscreen-API: ein fixed-Overlay per CSS-Klasse.
  // Grund: Auf iPadOS-Safari liegt die "nach unten wischen"-Geste zum Verlassen des
  // nativen Vollbilds ÜBER touch-action:none → vertikale Striche schlossen das Board.
  // Ein reines CSS-Overlay hat diese OS-Geste nicht, touch-action:none greift voll.
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((v) => !v);
  }, []);

  // Escape verlässt das Pseudo-Vollbild (natives Verhalten nachgebildet).
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  // Socket: vollen Status + Live-Ops empfangen.
  useEffect(() => {
    const s = socketRef.current;
    if (!s) return undefined;
    const onState = (full) => setState({ ops: full.ops || [], background: full.background || { kind: 'blank' } });
    const onOp = ({ op }) => setState(applyOp(stateRef.current, op));
    s.on('whiteboardState', onState);
    s.on('whiteboardOp', onOp);
    s.emit('getWhiteboardState', { roomId });
    return () => { s.off('whiteboardState', onState); s.off('whiteboardOp', onOp); };
  }, [socketRef, roomId, setState]);

  // --- Mod-Zeichenlogik ---
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#1f2d3d');
  const [width, setWidth] = useState(3);
  const drawing = useRef(null);
  const lastSent = useRef(0);

  const emitOp = useCallback((op) => {
    const s = socketRef.current;
    if (s) s.emit('whiteboardOp', { roomId, op });
  }, [socketRef, roomId]);

  const applyAndEmit = useCallback((op) => {
    setState(applyOp(stateRef.current, op));
    emitOp(op);
  }, [setState, emitOp]);

  const evtToNorm = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return normalizePoint(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);
  }, []);

  const onPointerDown = useCallback((e) => {
    if (readOnly) return;
    const p = evtToNorm(e);
    if (tool === 'pen' || tool === 'eraser') {
      const id = newId();
      drawing.current = { mode: 'stroke', id };
      applyAndEmit(makeStroke(id, color, width, tool === 'eraser', [p]));
      lastSent.current = Date.now();
    } else if (tool === 'text') {
      const content = window.prompt('Text eingeben:');
      if (content) applyAndEmit(makeText(newId(), p.x, p.y, content, color, Math.max(14, width * 6)));
    } else {
      drawing.current = { mode: 'shape', id: newId(), start: p };
    }
  }, [readOnly, tool, color, width, evtToNorm, applyAndEmit]);

  const onPointerMove = useCallback((e) => {
    if (readOnly || !drawing.current) return;
    const p = evtToNorm(e);
    if (drawing.current.mode === 'stroke') {
      const now = Date.now();
      if (now - lastSent.current >= 50) {
        applyAndEmit(makeStroke(drawing.current.id, color, width, tool === 'eraser', [p]));
        lastSent.current = now;
      } else {
        setState(applyOp(stateRef.current, makeStroke(drawing.current.id, color, width, tool === 'eraser', [p])));
      }
    }
  }, [readOnly, color, width, tool, evtToNorm, applyAndEmit, setState]);

  const onPointerUp = useCallback((e) => {
    if (readOnly || !drawing.current) return;
    const p = evtToNorm(e);
    if (drawing.current.mode === 'stroke') {
      applyAndEmit(makeStroke(drawing.current.id, color, width, tool === 'eraser', [p]));
    } else if (drawing.current.mode === 'shape') {
      const start = drawing.current.start;
      applyAndEmit(makeShape(drawing.current.id, tool, start.x, start.y, p.x, p.y, color, width));
    }
    drawing.current = null;
  }, [readOnly, color, width, tool, evtToNorm, applyAndEmit]);

  return (
    <div ref={containerRef} className={`wb-container${isFullscreen ? ' wb-container--fs' : ''}`}>
      <canvas
        ref={canvasRef}
        className="wb-canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{ cursor: readOnly ? 'default' : 'crosshair' }}
      />

      {/* Bestätigungs-Overlay bleibt im Container → kein Fullscreen-Exit */}
      {showClearConfirm && (
        <div className="wb-confirm-overlay">
          <div className="wb-confirm">
            <p>Whiteboard wirklich leeren?</p>
            <div className="wb-confirm-buttons">
              <button className="wb-confirm-ok" onClick={() => { applyAndEmit(makeClear()); setShowClearConfirm(false); }}>
                Leeren
              </button>
              <button className="wb-confirm-cancel" onClick={() => setShowClearConfirm(false)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="wb-toolbar">
          {['pen', 'eraser', 'line', 'rect', 'ellipse', 'arrow', 'text'].map((t) => (
            <button key={t} className={tool === t ? 'active' : ''} onClick={() => setTool(t)}>
              {{ pen: '✏️', eraser: '🧽', line: '／', rect: '▭', ellipse: '◯', arrow: '➜', text: 'T' }[t]}
            </button>
          ))}
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} title="Farbe" />
          <input type="range" min="1" max="20" value={width} onChange={(e) => setWidth(Number(e.target.value))} title="Strichstärke" />
          <select
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'image-upload') { document.getElementById('wb-file-input').click(); }
              else { applyAndEmit(makeSetBackground(v)); }
              e.target.value = 'keep';
            }}
            defaultValue="keep"
            title="Hintergrund"
          >
            <option value="keep" disabled>Hintergrund…</option>
            <option value="blank">Weiß</option>
            <option value="grid">Raster</option>
            <option value="dots">Punkte</option>
            <option value="staff">Notenlinien</option>
            <option value="image-upload">Bild hochladen…</option>
          </select>
          <input
            id="wb-file-input" type="file" accept="image/*" style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files[0];
              if (file && onUploadImage) {
                try {
                  const url = await onUploadImage(file);
                  if (url) applyAndEmit(makeSetBackground('image', url));
                } catch (err) { window.alert('Upload fehlgeschlagen: ' + err.message); }
              }
              e.target.value = '';
            }}
          />
          <button onClick={() => setShowClearConfirm(true)} title="Leeren">🗑️</button>
          <button onClick={toggleFullscreen} title={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}>
            {isFullscreen ? '⊡' : '⛶'}
          </button>
        </div>
      )}
    </div>
  );
}
